/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { convertHexStringToNumber } from 'utils/color-utils';
import { utils } from 'pixi.js-legacy';
import { cssVar } from '@touchplan/css-var';
import { FancyTextOptions, FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { ICanvasDestroyable, AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { PLAN_CONSTANTS } from '@touchplan/common';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';

export interface IClipboardDragFeedbackSettings {
	lineHeight: number;
	lineAndTicketMargin: number;
	leftTextMargin: number;
	rightTextMargin: number;
	backgroundColor: string;
	backgroundAlpha?: number;
}
export const CLIPBOARD_DRAG_FEEDBACK_SETTINGS: IClipboardDragFeedbackSettings = {
	lineHeight: 22,
	lineAndTicketMargin: 10,
	leftTextMargin: 30,
	rightTextMargin: 30,
	backgroundColor: '#0073ad',
	backgroundAlpha: 1.0,
};

export class ClipboardDragLine implements ICanvasDestroyable {
	protected parentApplication: AbstractApplication;

	public line: FancySprite;
	public leftText: HighResolutionText;
	public centerText: HighResolutionText;
	public rightText: HighResolutionText;

	constructor(labelText: string, parent: FancyContainer, parentApplication: AbstractApplication) {
		this.parentApplication = parentApplication;

		this.line = this.createLineSprite();

		this.leftText = this.createText();
		this.centerText = this.createText();
		this.rightText = this.createText();

		parent.addChild(this.line);
		parent.addChild(this.leftText);
		parent.addChild(this.centerText);
		parent.addChild(this.rightText);

		this.y = 0;
		this.left = '';
		this.center = labelText;
		this.right = '';
		this.updateLayout();
	}

	set y(val: number) {
		// All relative to the parent container
		this.line.y = val;
		this.leftText.y = val;
		this.centerText.y = val;
		this.rightText.y = val;
	}

	set left(val: string) {
		this.leftText.text = val;
	}

	set center(val: string) {
		this.centerText.text = val;
	}

	set right(val: string) {
		this.rightText.text = val;
	}

	protected createLineSprite(): FancySprite {
		const result = new FancySprite(utils.TextureCache['ticket']);
		result.x = 0; // Relative to the parent container
		result.visible = true;
		result.tint = convertHexStringToNumber(CLIPBOARD_DRAG_FEEDBACK_SETTINGS.backgroundColor);
		result.alpha = CLIPBOARD_DRAG_FEEDBACK_SETTINGS.backgroundAlpha;
		result.eventMode = EventMode.Static;
		return result;
	}

	protected createText(): HighResolutionText {
		const options: FancyTextOptions = {
			fontSize: cssVar.h3FontSize_px,
			fill: cssVar.white_number,
			fontFamily: cssVar.fontFamily,
		};
		const result = new HighResolutionText('', new FancyTextStyle(options));
		result.x = 0;
		result.resolution = 2;
		return result;
	}

	public updateLayout(): void {
		// Offset from the sidebar:
		const offsetWidth = this.getScreenWidth() - PLAN_CONSTANTS.SIDEBAR_WIDTH;

		this.line.width = offsetWidth;
		this.line.height = CLIPBOARD_DRAG_FEEDBACK_SETTINGS.lineHeight;

		this.leftText.x = CLIPBOARD_DRAG_FEEDBACK_SETTINGS.leftTextMargin;
		this.centerText.x = (offsetWidth - this.centerText.width) / 2;
		this.rightText.x =
			offsetWidth - (this.rightText.width + CLIPBOARD_DRAG_FEEDBACK_SETTINGS.rightTextMargin);
	}

	public getScreenWidth(): number {
		const renderer = this.parentApplication.renderer;
		return renderer.width / renderer.resolution;
	}

	public destroy(opts?: any): void {
		this.line.destroy();
		this.leftText.destroy();
		this.centerText.destroy();
		this.rightText.destroy();

		this.line = null;
		this.leftText = null;
		this.centerText = null;
		this.rightText = null;
	}
}
